<script setup lang="ts">
  import { capitalize } from '@/helpers/converter'
  import { classList, ICharacterCdn, IClassJobCdn, useCJStore } from '@/store/ClassesJobs'
  import { useGAEventStore } from '@/store/GAEventTracking'
  import 'swiper/css'
  import { Controller, Navigation } from 'swiper/modules'
  import { Swiper as SwiperClass } from 'swiper/types'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useDisplay } from 'vuetify'
  const { mobile } = useDisplay()
  const cjStore = useCJStore()
  const swiperRef = ref<SwiperClass>()
  const mobileSwiperRef = ref<SwiperClass>()
  const mobileContentSwiperRef = ref<SwiperClass>()
  const swipers: SwiperClass[] = [swiperRef, mobileSwiperRef, mobileContentSwiperRef]
  const refDom = ref()

  const props = defineProps({
    className: {
      type: String,
      required: true,
    },
    classId: {
      type: Number,
    },
    trackArrows: {
      type: Boolean,
      default: false,
    },
  })
  const emits = defineEmits(['change:className', 'change:classId'])

  const classesJobsList = computed<IClassJobCdn[]>((): IClassJobCdn[] => cjStore.classesJobsList)
  const filteredJobList = computed<ICharacterCdn[]>(
    (): ICharacterCdn[] =>
      classesJobsList.value.find((item: IClassJobCdn) => item.name.toLowerCase() === props.className)?.Children || []
  )

  const selectedClass = ref(props.className)
  const selectedJob = computed(() => filteredJobList.value.find((item: ICharacterCdn) => item.id === props.classId))
  const setSwiperRef = (swiper: SwiperClass) => (swiperRef.value = swiper)
  const setMobileSwiperRef = (swiper: SwiperClass) => (mobileSwiperRef.value = swiper)
  const setMobileContentSwiperRef = (swiper: SwiperClass) => (mobileContentSwiperRef.value = swiper)
  const getEmblemSrc = (className: string): URL =>
    new URL(`/src/assets/imgs/classesJobs/emblems/${className}.png`, import.meta.url)

  const gaStore = useGAEventStore()

  onMounted(async () => {
    window.addEventListener('resize', slideToCharacter)
  })
  onUnmounted(() => {
    window.removeEventListener('resize', slideToCharacter)
  })

  const slideToCharacter = () => {
    updateSwipers()
    selectedClass.value = props.className
    filteredJobList.value.find((item: ICharacterCdn, index: number) => {
      if (item.id === props.classId) {
        slideToSwipers(index)
      }
    })
  }
  const updateSwipers = () => {
    swipers.forEach((swiper: SwiperClass) => swiper.value.update())
  }
  const slideToSwipers = (index: number) => {
    swipers.forEach((swiper: SwiperClass) => swiper.value.slideTo(index))
  }
  const selectClass = (className: string) => {
    if (props.className === className) return
    emits('change:className', className)
  }
  const selectCharacter = (classId: number) => {
    if (props.classId === classId) return
    emits('change:classId', classId)
  }

  watch(props, ({ className, classId }: { className: string; classId?: number }) => {
    if (className && classId) slideToCharacter()
  })

  watch(selectedClass, (val, oldVal) => {
    if (oldVal) {
      gaStore.pushActionEvent(
        {
          interaction: 'filter_select',
          sectionContent: val,
        },
        refDom.value
      )
    }
  })
  watch(
    () => props.classId,
    (val, oldVal) => {
      if (val && oldVal && selectedJob.value && selectedJob.value.name) {
        gaStore.pushActionEvent(
          {
            interaction: 'menu_click',
            sectionContent: selectedJob.value.name,
          },
          refDom.value
        )
      }
    }
  )

  const updateSwiper = (swiper: SwiperClass) => {
    if (!mobile.value) return
    selectCharacter(filteredJobList.value[swiper.activeIndex].id)
  }
</script>
<template>
  <div ref="refDom" class="cj-selection">
    <div>
      <div class="class-dropdown-container">
        <span class="class-title">CLASS:</span>
        <v-select
          v-model="selectedClass"
          class="class-dropdown"
          :class="`${className}`"
          hide-details
          hide-selected
          :items="classList"
          :menu-props="{
            closeonClick: true,
            closeOnContentClick: true,
            contentClass: 'cj-dropdown-content',
          }"
          :variant="'outlined'"
        >
          <template #selection="{ item }">
            <div :class="`cj-dropdown cj-dropdown-${item.title}`">
              <img :alt="item.title" :src="getEmblemSrc(item.title)" />
              {{ capitalize(className) }}
            </div>
          </template>
          <template #item="{ item }">
            <div
              :class="`cj-dropdown cj-dropdown-item cj-dropdown-${item.title}`"
              @click="() => selectClass(item.value)"
            >
              <img :alt="item.title" :src="getEmblemSrc(item.title)" />
              {{ capitalize(item.title) }}
            </div>
          </template>
          <template #append-inner>
            <img src="@/assets/imgs/classesJobs/arrow.png" />
          </template>
        </v-select>
      </div>
      <div class="job-selection-container">
        <span class="jobs-title">JOBS:</span>
        <swiper
          v-show="!mobile"
          class="cj-swiper"
          :controller="{ control: swiperRef }"
          :direction="'horizontal'"
          :modules="[Navigation, Controller]"
          :navigation="{ prevEl: '.cj-swiper-prev', nextEl: '.cj-swiper-next' }"
          :slides-per-view="'auto'"
          @swiper="setSwiperRef"
        >
          <swiper-slide
            v-for="job in filteredJobList"
            :key="job.id"
            :class="{ selected: job.id === classId }"
            @click="selectCharacter(job.id)"
          >
            <LazyLoadImage
              class="job-thumb__thumbnail"
              :class="`${className}`"
              :src="job.characterThumbnail"
            ></LazyLoadImage>
            <div class="job-name">{{ job.name }}</div>
          </swiper-slide>
        </swiper>
        <div
          v-ga-event="props.trackArrows ? { sectionContent: 'Arrows' } : null"
          class="cj-swiper-nav cj-swiper-prev"
        ></div>
        <div
          v-ga-event="props.trackArrows ? { sectionContent: 'Arrows' } : null"
          class="cj-swiper-nav cj-swiper-next"
        ></div>
        <swiper
          v-show="mobile"
          :centered-slides="true"
          class="cj-swiper cj-swiper-selection"
          :controller="{ control: mobileContentSwiperRef }"
          :direction="'horizontal'"
          :modules="[Navigation, Controller]"
          :navigation="{ prevEl: '.cj-swiper-prev', nextEl: '.cj-swiper-next' }"
          :slides-per-view="'auto'"
          @slide-change="updateSwiper"
          @swiper="setMobileSwiperRef"
        >
          <swiper-slide
            v-for="job in filteredJobList"
            :key="job.id"
            :class="{ selected: job.id === classId }"
            @click="selectCharacter(job.id)"
          >
            <div class="job-name">{{ job.name }}</div>
          </swiper-slide>
        </swiper>
        <swiper
          v-show="mobile"
          :auto-height="true"
          :centered-slides="true"
          class="cj-swiper cj-swiper-content"
          :controller="{ control: mobileSwiperRef }"
          :direction="'horizontal'"
          :modules="[Controller]"
          :slides-per-view="'auto'"
          @swiper="setMobileContentSwiperRef"
        >
          <swiper-slide v-for="job in filteredJobList" :key="job.id">
            <slot :character="job" name="mobile-content" />
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  $imgPath: '@/assets/imgs/classesJobs';
  $jobs: (
    warrior: var(--job-warriorColor),
    magician: var(--job-magicianColor),
    bowman: var(--job-bowmanColor),
    thief: var(--job-thiefColor),
    pirate: var(--job-pirateColor),
  );
  $jobsHover: (
    warrior: #0b4269,
    magician: #215153,
    bowman: #443b1c,
    thief: #71260f,
    pirate: #521071,
  );
  $jobsThumbnailBg: (
    warrior: #a5c7da,
    magician: #a3dbaa,
    bowman: #e6dcb2,
    thief: #f4d5bc,
    pirate: #c4d0ea,
  );

  .cj-selection {
    height: pxToRem(250px);
    background-color: #4c4136;
    padding: 0 pxToRem(166.325px);
    line-height: pxToRem(54.425px);
    font-size: pxToRem(30.25px);
    font-family: Glegoo;
    color: #fff;
    font-weight: bold;
    z-index: 1;
    overflow: hidden;
    > div {
      max-width: 1600px;
      margin: auto;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
    }
  }
  .class-dropdown-container {
    display: flex;
    align-items: center;
  }
  .class-dropdown {
    width: pxToRem(197.575px);
    color: #fff;
    margin-left: pxToRem(12.5px);
    :deep(.v-field__input) {
      min-height: pxToRem(40.325px);
      padding: 0;
      .v-select__selection {
        margin: 0;
        font-size: pxToRem(18.15px);
        width: 100%;
      }
    }
    @each $key, $val in $jobs {
      &.#{$key} :deep(.v-input__control) {
        background-color: #{$val};
      }
    }
    :deep(.v-field__outline) {
      border-radius: 0;
      border: pxToRem(1px) solid #fff;
      .v-field__outline__start,
      .v-field__outline__end {
        border: none;
      }
    }
    :deep(.v-field__append-inner) {
      margin-right: pxToRem(3.75px);
      img {
        width: pxToRem(14.125px);
        height: pxToRem(9.075px);
        margin: auto;
        transition: 0.2s linear;
      }
      i {
        display: none;
      }
    }
    &.v-select--active-menu {
      .v-field__append-inner img {
        transform: scaleY(-1);
      }
    }
  }
  .job-selection-container {
    margin-left: pxToRem(81.25px);
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    position: relative;
  }
  .cj-swiper {
    margin-left: pxToRem(87.5px);
    height: 100%;
    width: pxToRem(982.875px);
    .swiper-slide {
      width: pxToRem(151.2px) !important;
      height: 100%;
      font-size: pxToRem(16.125px);
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      cursor: pointer;
      &.selected {
        .job-thumb__thumbnail {
          border: pxToRem(4px) solid #fea922;
        }
      }
      &:not(:last-child) {
        margin-right: pxToRem(15.125px);
      }
    }
  }
  .job-name {
    height: pxToRem(43.75px);
    text-align: center;
    line-height: pxToRem(18.75px);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: pxToRem(12.5px);
  }
  .job-thumb__thumbnail {
    position: absolute;
    left: 0;
    top: pxToRem(37.5px);
    width: 100%;
    height: pxToRem(151.2px);
    z-index: 0;
    background: center center/ 100% auto;
    background-repeat: no-repeat;
    border-radius: pxToRem(3.75px);
    transition: background 0.2s ease;
    @each $key, $val in $jobsThumbnailBg {
      &.#{$key} {
        background-color: $val;
      }
    }
    &:hover {
      background-size: 110%;
    }
    &:before {
      border-radius: pxToRem(3.75px);
    }
  }
  .cj-swiper-nav {
    width: pxToRem(29.225px);
    height: pxToRem(46.375px);
    background: url(#{$imgPath}/carousel_arrow.png) center center / 100% auto;
    background-repeat: no-repeat;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 2;
    &.cj-swiper-prev {
      left: pxToRem(112.5px);
    }
    &.cj-swiper-next {
      right: pxToRem(0px);
      transform: translateY(-50%) scaleX(-1);
    }
    &:hover {
      background-image: url(#{$imgPath}/carousel_arrow_hover.png);
    }
    &.swiper-button-disabled {
      background-image: url(#{$imgPath}/carousel_arrow.png);
      cursor: default;
    }
  }

  .cj-dropdown {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    img {
      margin: 0 pxToRem(6.25px);
    }
    &.cj-dropdown-warrior {
      img {
        width: pxToRem(17.125px);
        height: pxToRem(20.15px);
      }
    }
    &.cj-dropdown-magician {
      img {
        width: pxToRem(22.175px);
        height: pxToRem(23.175px);
      }
    }
    &.cj-dropdown-bowman {
      img {
        width: pxToRem(26.2px);
        height: pxToRem(24.2px);
      }
    }
    &.cj-dropdown-thief {
      img {
        width: pxToRem(30.25px);
        height: pxToRem(13.1px);
      }
    }
    &.cj-dropdown-pirate {
      img {
        width: pxToRem(24.2px);
        height: pxToRem(22.175px);
      }
    }
  }
  @each $key, $val in $jobs {
    .cj-dropdown-#{$key} {
      background-color: #{$val};
    }
  }
  .cj-dropdown-item {
    height: pxToRem(40.325px);
    line-height: pxToRem(32.25px);
    font-size: pxToRem(18.15px);
    color: #fff;
    font-family: Glegoo;
    border-bottom: pxToRem(1px) solid #fff;
    @each $key, $val in $jobsHover {
      &.cj-dropdown-#{$key}:hover {
        background-color: #{$val};
      }
    }
  }

  @media #{$tablet} {
    .cj-selection {
      height: pxToRemTablet(148.01px);
      font-size: pxToRemTablet(15.998px);
      padding: 0 pxToRemTablet(76px);
    }
    .class-dropdown {
      width: pxToRemTablet(104.994px);
      margin-left: pxToRemTablet(9.5px);
      :deep(.v-field) {
        padding-right: pxToRemTablet(1.9px);
      }
      :deep(.v-field__input) {
        min-height: pxToRemTablet(28.5px);
        .v-select__selection {
          font-size: pxToRemTablet(13.984px);
        }
      }
      :deep(.v-field__outline) {
        border: pxToRemTablet(0.608px) solid #fff;
        .v-field__outline__start,
        .v-field__outline__end {
          border: none;
        }
      }
      :deep(.v-field__append-inner) {
        margin-right: pxToRemTablet(3.8px);
        img {
          width: pxToRemTablet(6.764px);
          height: pxToRemTablet(4.18px);
        }
      }
    }

    .jobs-title,
    .job-name {
      display: none;
    }
    .job-selection-container {
      margin-left: pxToRemTablet(76px);
    }
    .cj-swiper {
      margin: 0;
      width: pxToRemTablet(624.986px);
      .swiper-slide {
        width: pxToRemTablet(96.254px) !important;
        height: pxToRemTablet(96.254px);
        &.selected {
          .job-thumb__thumbnail {
            border-width: pxToRemTablet(7.6px);
          }
        }
        &:not(:last-child) {
          margin-right: pxToRemTablet(9.5px);
        }
      }
    }
    .job-thumb__thumbnail {
      height: pxToRemTablet(96.254px);
      top: pxToRemTablet(26.6px);
      border-radius: pxToRemTablet(5.7px);
    }
    .cj-swiper-nav {
      width: pxToRemTablet(15.01px);
      height: pxToRemTablet(24.016px);
      &.cj-swiper-prev {
        left: pxToRemTablet(-32.3px);
      }
      &.cj-swiper-next {
        right: pxToRemTablet(-28.5px);
      }
    }
    .cj-dropdown {
      img {
        margin: 0 pxToRemTablet(3.8px);
      }
      &.cj-dropdown-warrior {
        img {
          width: pxToRemTablet(9.082px);
          height: pxToRemTablet(8.246px);
        }
      }
      &.cj-dropdown-magician {
        img {
          width: pxToRemTablet(11.932px);
          height: pxToRemTablet(12.35px);
        }
      }
      &.cj-dropdown-bowman {
        img {
          width: pxToRemTablet(12.008px);
          height: pxToRemTablet(11.096px);
        }
      }
      &.cj-dropdown-thief {
        img {
          width: pxToRemTablet(12.008px);
          height: pxToRemTablet(5.206px);
        }
      }
      &.cj-dropdown-pirate {
        img {
          width: pxToRemTablet(12.008px);
          height: pxToRemTablet(10.982px);
        }
      }
    }
    .cj-dropdown-item {
      font-size: pxToRemTablet(13.984px);
      height: pxToRemTablet(28.5px);
      line-height: 0.75;
      border-bottom: pxToRemTablet(0.988px) solid #fff;
    }
  }

  @media #{$mobile} {
    .cj-selection {
      height: auto;
      padding: 0;
      background-color: transparent;
      > div {
        flex-direction: column;
      }
    }
    .class-dropdown {
      margin: 0;
      width: pxToRemMobile(194.004px);
      :deep(.v-field) {
        padding-right: pxToRemMobile(0.9px);
      }
      :deep(.v-field__input) {
        min-height: pxToRemMobile(39.996px);
        .v-select__selection {
          font-size: pxToRemMobile(18px);
        }
      }
      :deep(.v-field__outline) {
        border: pxToRemMobile(1.008px) solid #fff;
        .v-field__outline__start,
        .v-field__outline__end {
          border: none;
        }
      }
      :deep(.v-field__append-inner) {
        margin-right: pxToRemMobile(18px);
        img {
          width: pxToRemMobile(12.6px);
          height: pxToRemMobile(7.848px);
        }
      }
    }
    .cj-dropdown {
      img {
        margin: 0 pxToRemMobile(9px);
      }
      &.cj-dropdown-warrior {
        img {
          width: pxToRemMobile(16.992px);
          height: pxToRemMobile(19.998px);
        }
      }
      &.cj-dropdown-magician {
        img {
          width: pxToRemMobile(21.996px);
          height: pxToRemMobile(23.004px);
        }
      }
      &.cj-dropdown-bowman {
        img {
          width: pxToRemMobile(25.992px);
          height: pxToRemMobile(23.994px);
        }
      }
      &.cj-dropdown-thief {
        img {
          width: pxToRemMobile(30.006px);
          height: pxToRemMobile(12.996px);
        }
      }
      &.cj-dropdown-pirate {
        img {
          width: pxToRemMobile(23.994px);
          height: pxToRemMobile(21.996px);
        }
      }
    }
    .cj-dropdown-item {
      font-size: pxToRemMobile(18px);
      height: pxToRemMobile(39.996px);
      line-height: 0.75;
      border-bottom: pxToRemMobile(1.008px) solid #fff;
    }
    .class-title {
      display: none;
    }
    .job-name {
      display: flex;
      height: pxToRemMobile(54px);
      text-transform: uppercase;
    }
    .job-selection-container {
      margin: 0;
      flex-direction: column;
    }
    .cj-swiper {
      width: 100%;
      height: auto;
      margin-top: pxToRemMobile(18px);
      .swiper-slide {
        width: auto !important;
        height: auto;
        font-size: pxToRemMobile(39.996px);
        color: rgba(255, 255, 255, 0.5);
        font-family: Glory;
        font-weight: bold;
        &.selected {
          color: #fff;
          font-size: pxToRemMobile(48.006px);
        }
      }
    }
    .cj-swiper-nav {
      width: pxToRemMobile(28.008px);
      height: pxToRemMobile(45px);
      top: 50%;
      &.cj-swiper-prev {
        left: pxToRemMobile(18px);
      }
      &.cj-swiper-next {
        right: pxToRemMobile(18px);
      }
    }
    .cj-swiper-selection {
      width: auto;
      margin-top: pxToRemMobile(27px);
      .swiper-slide {
        margin: 0 pxToRemMobile(18px) !important;
      }
    }
    .cj-swiper-content {
      margin: pxToRemMobile(9px) 0 0;
      .swiper-slide {
        width: 100% !important;
        align-items: center;
        justify-content: flex-start;
        margin: 0 !important;
      }
    }
  }
</style>
<style lang="scss">
  .cj-dropdown-content {
    border-top: pxToRem(1px) solid #fff;
    border-left: pxToRem(1px) solid #fff;
    border-right: pxToRem(1px) solid #fff;
    border-radius: 0 !important;
    > div {
      padding: 0;
    }
  }
  @media #{$tablet} {
    div.cj-dropdown-content {
      border-top: pxToRemTablet(0.988px) solid #fff;
      border-left: pxToRemTablet(0.988px) solid #fff;
      border-right: pxToRemTablet(0.988px) solid #fff;
    }
  }
  @media #{$mobile} {
    div.cj-dropdown-content {
      border-top: pxToRemMobile(1.008px) solid #fff;
      border-left: pxToRemMobile(1.008px) solid #fff;
      border-right: pxToRemMobile(1.008px) solid #fff;
    }
  }
</style>
